/**
 * @fileoverview Hook de UI Layer para obtener y actualizar la duración formateada de la llamada Fonema en tiempo real.
 * @module useFonemaCallDuration
 * @category Hook - UI Layer
 * @since 1.0.0
 * @version 1.0.0
 */

import { useEffect, useState } from "react";

/**
 * Hook de UI Layer para gestión de duración de llamada Fonema.
 *
 * Implementa el patrón de **UI Layer** con separación estricta de responsabilidades,
 * enfocándose únicamente en exponer la duración formateada y su actualización periódica.
 *
 * @function useFonemaCallDuration
 * @category Hook - UI Layer
 *
 * @description
 * **Arquitectura Optimizada (v1.0.0):**
 * - ✅ Solo UI Layer: expone duración formateada, sin lógica de negocio ni API
 * - ✅ Performance: setInterval con limpieza y memoización
 * - ✅ Reutilizable en cualquier componente de Fonema
 *
 * **Responsabilidades:**
 * - Proveer la duración formateada de la llamada en tiempo real
 * - Actualizar cada segundo usando el helper FonemaWhatsappAudio
 *
 * **NO Maneja:**
 * - ❌ Lógica de negocio
 * - ❌ Llamadas a API
 * - ❌ Estados de otros componentes
 *
 * @returns {Object} Objeto con la duración formateada { callDuration: string }
 *
 * @example
 * const { callDuration } = useFonemaCallDuration();
 * <span>{callDuration}</span>
 *
 * @see {@link FonemaWhatsappAudio.getFormattedCallDuration} Helper utilizado
 *
 * @author Mercately Team
 * @copyright 2024 Mercately
 */

/**
 * Hook para exponer la duración de llamada.
 */
export default function useFonemaCallDuration() {
  const [callDuration, setCallDuration] = useState('00:00:00');

  useEffect(() => {
    let isMounted = true;
    const updateDuration = () => {
      if (!window.FonemaWhatsappAudio || !window.FonemaWhatsappAudio.getFormattedCallDuration) {
        return;
      }

      const formatted = window.FonemaWhatsappAudio.getFormattedCallDuration();
      if (isMounted) setCallDuration(formatted);
    };
    updateDuration();
    const interval = setInterval(updateDuration, 1000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  return { callDuration };
}
